import React, { useEffect, useState } from 'react';
import { Select, Input } from "antd";

import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

import './Tracker.css';
import { ESD_LIBRARY } from '../data/esdLibrary';
import './Simulator.css';
import { onboardingAPI, vesselAPI, simulationAPI, userAPI, makeRequest } from '../services/apiService';
import SimulationWorkspace from './SimulationWorkspace';


const { Option } = Select;
const mapApiVesselToLocal = (raw = {}, index = 0) => {
  const v = raw.vessel || raw;
  const meta = raw.voyage_meta || raw.voyageMeta || {};
  const machines = raw.machines || [];
  const esdMeasures = raw.esd_measures || raw.esdMeasures || [];

  return {
    id: raw.id ?? v.id ?? `v${index}`,
    month: meta.analysis_month ?? '',
    year: meta.analysis_year ?? new Date().getFullYear(),
    dockMonth: meta.docking_month ?? '',
    owner: v.name_of_owner ?? '',
    vesselName: v.vessel_name ?? '',
    vesselType: v.vessel_type ?? '',
    buildYear: v.build_year ?? '',
    flag: v.flag ?? '',
    classificationSociety: v.classification_society ?? '',
    imoNumber: v.imo_number ?? '',
    grossTonnage: v.gross_tonnage ?? '',
    deadWeight: v.dead_weight ?? '',
    sailingDays: meta.sailing_days_per_year ?? raw.sailing_days_per_year ?? '',
    nonSteamingDays: meta.non_steaming_days_per_year ?? raw.non_steaming_days_per_year ?? '',
    distanceNm: meta.distance_nm ?? '',
    euPct: meta.eu_voyages_percent ?? raw.eu_voyages_percent ?? '',
    euaCost: meta.eua_cost_usd ?? raw.eua_cost_usd ?? '',
    lastReport: raw.last_report ?? raw.lastReport ?? null,
    lastReportDate: raw.last_report_date ?? null,
    machines,
    // Keep full ESD objects from saved data so user-edited values are preserved.
    // Match each saved ESD to its library entry by name to get the id for checkbox sync.
    selectedEsds: esdMeasures.map((e) => {
      const libMatch = ESD_LIBRARY.find(lib => lib.name === e.name);
      return {
        id: e.id || libMatch?.id || e.name,
        name: e.name,
        category: e.category,
        saving: e.efficiency_gain_percent || e.saving || 0,
        capex: e.cost_usd || e.capex || 0,
        efficiency_gain_percent: e.efficiency_gain_percent || e.saving || 0,
        cost_usd: e.cost_usd || e.capex || 0,
        lead_time_months: e.lead_time_months || 4,
        installation_req: e.installation_req || 'in_sailing',
      };
    }),
  };
};


const DRAFT_STORAGE_KEY = 'azolla_onboard_draft_v1';

const createFuel = () => ({
  fuelName: "",
  consumption: "",
  price: ""
});

const createMachine = (name = "") => ({
  machineName: name,
  fuels: [createFuel()]
});

const getDefaultMachines = () => [
  createMachine("Main engine"),
  createMachine("Auxiliary engine"),
];

const getDefaultFormData = () => ({
  month: '',
  year: new Date().getFullYear(),
  dockMonth: '',
  owner: '',
  vesselName: '',
  vesselType: '',
  buildYear: '',
  flag: '',
  classificationSociety: '',
  imoNumber: '',
  grossTonnage: '',
  deadWeight: '',
  sailingDays: '200',
  nonSteamingDays: '165',
  distanceNm: '60000',
  euPct: '30',
  euaCost: '75',
  fI: '1.0',
  fM: '1.0',
  fC: '1.0',
  fIvse: '1.0',
  vesselLifeYears: '25',
  vesselEndYear: '',
  vesselEndMonth: '',
  dockingYear: '',
  commonImplMonth: '',
  commonImplYear: '',
  discountRate: '0.10',
});

function loadOnboardDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    console.warn('[onboard draft] Could not parse saved draft:', e);
    return null;
  }
}




function Tracker({ userEmail, isAdmin = false, onLogout }) {
  const savedDraftRef = React.useRef(loadOnboardDraft());
  const savedDraft = savedDraftRef.current;

  const [activeTab, setActiveTab] = useState('vessels');
  const [vessels, setVessels] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [simulatingId, setSimulatingId] = useState(null);
  // ESD measure selections are intentionally NOT restored from the draft —
  // only the raw form fields and machines should persist across a back/forward.
  const [selectedEsds, setSelectedEsds] = useState([]);
  // "Assign to" user — same rule as ESD measures: never restored from the
  // draft, always reset when a fresh onboarding form is opened.
  const [assignedUserId, setAssignedUserId] = useState(null);
  // Email of the currently-selected assignee — used to re-resolve their id
  // against a freshly fetched user list right before saving, in case ids
  // shifted since the dropdown was first opened.
  const [assignedUserEmail, setAssignedUserEmail] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  // Vessel cover photo for the PDF report — kept separate from formData
  // (like ESDs/assignee above) so it's never written into the localStorage
  // draft (base64 images are large) and always resets on a fresh form.
  const [vesselImageBase64, setVesselImageBase64] = useState(null);
  const [vesselImageFileName, setVesselImageFileName] = useState(null);
  const [vesselImageError, setVesselImageError] = useState(null);
  const [vesselsLoading, setVesselsLoading] = useState(true);
  const [vesselsError, setVesselsError] = useState(null);
  const [simulationData, setSimulationData] = useState(null);
  const [selectedSection, setSelectedSection] = useState("esd");
  const [selectedItem, setSelectedItem] = useState(null);

  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  // Reports modal state
  const [reportsModalOpen, setReportsModalOpen] = useState(false);
  const [reportsVessel, setReportsVessel] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsList, setReportsList] = useState([]);

  const openReportsModal = async (vessel) => {
    setReportsVessel(vessel);
    setReportsList([]);
    setReportsModalOpen(true);
    setReportsLoading(true);
    try {
      const result = await simulationAPI.listReports(vessel.id);
      const reports = result?.data?.data || result?.data || result || [];
      setReportsList(Array.isArray(reports) ? reports : []);
    } catch (err) { console.warn('Could not fetch reports:', err); }
    setReportsLoading(false);
  };

  // Report currently being turned into a PDF, so its row can show a spinner
  // and other rows stay clickable.
  const [pdfGeneratingId, setPdfGeneratingId] = useState(null);

  // True while a "Download PDF" click is quietly loading the report into the
  // simulator in the background so its CII / financial charts can render
  // before generateReport() captures them.
  const [autoExportPdfPending, setAutoExportPdfPending] = useState(false);

  const downloadReport = async (report, vessel) => {
    const reportKey = report.report_id || report.id;
    setPdfGeneratingId(reportKey);
    setReportsModalOpen(false);

    // Fetch the full report (report list entries are often summaries) —
    // same as confirmSession() does when opening the simulator normally.
    let fullReport = report;
    if (report?.report_id) {
      try {
        const fullReportResult = await simulationAPI.getReport(report.report_id);
        if (fullReportResult.success) {
          fullReport = fullReportResult.data?.data || fullReportResult.data || report;
        }
      } catch (err) {
        console.warn('Could not fetch full report for PDF export:', err.message);
      }
    }

    setSimulatingId(vessel.id);
    setSessionMode('last');
    setVesselReports([fullReport]);
    setInitialReport(fullReport);
    setAutoExportPdfPending(true);
    setActiveTab('simulator');
  };

  const simulateFromReport = async (report, vessel) => {
    setReportsModalOpen(false);
    setSimulatingId(vessel.id);
    setSessionMode('last');
    setVesselReports([report]);
    setSessionModalOpen(true);
  };


  useEffect(() => {


    loadVessels();
  }, []);



  const [machines, setMachines] = useState(savedDraft?.machines || getDefaultMachines());

  const [formData, setFormData] = useState(savedDraft?.formData || getDefaultFormData());


  useEffect(() => {
    try {
      // Only form values and machines are drafted — ESD measures are excluded on purpose.
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ formData, machines }));
    } catch (e) {
      console.warn('[onboard draft] Could not save draft:', e);
    }
  }, [formData, machines]);

  const resetForm = () => {
    setFormData(getDefaultFormData());
    setMachines(getDefaultMachines());
    setSelectedEsds([]);
    setAssignedUserId(null);
    setAssignedUserEmail(null);
    setVesselImageBase64(null);
    setVesselImageFileName(null);
    setVesselImageError(null);
    setEditingId(null);
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (e) { }
  };

  // Reads a selected image file, downsizes it if needed, and stores it as a
  // base64 data URL — this goes straight into the onboard payload and later
  // gets embedded on the PDF report's cover page.
  const MAX_VESSEL_IMAGE_MB = 5;
  const handleVesselImageSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setVesselImageError(null);
    if (!file.type.startsWith('image/')) {
      setVesselImageError('Please choose an image file (PNG or JPG).');
      return;
    }
    if (file.size > MAX_VESSEL_IMAGE_MB * 1024 * 1024) {
      setVesselImageError(`Image is too large — please choose one under ${MAX_VESSEL_IMAGE_MB}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setVesselImageBase64(reader.result);
      setVesselImageFileName(file.name);
    };
    reader.onerror = () => setVesselImageError('Could not read that image — please try again.');
    reader.readAsDataURL(file);
  };

  // Lazily fetches the assignable-user list the first time the "Assign to
  // user" dropdown is opened, so it isn't called on every render/re-open.
  // Pass { force: true } to refetch even if already loaded (used right
  // before saving, to resolve against the freshest data).
  const loadUsersIfNeeded = async ({ force = false } = {}) => {
    if (!force && (usersLoaded || usersLoading)) return userOptions;
    setUsersLoading(true);
    let list = userOptions;
    try {
      const result = await userAPI.list();
      list = result?.data?.data || result?.data || result || [];
      list = Array.isArray(list) ? list : [];
      setUserOptions(list);
      setUsersLoaded(true);
    } catch (err) {
      console.warn('Could not fetch users:', err);
    }
    setUsersLoading(false);
    return list;
  };

  const openOnboardModal = (vesselId = null) => {
    if (vesselId) {
      const vessel = vessels.find(v => v.id === vesselId);
      if (vessel) {
        setFormData(vessel);
        setEditingId(vesselId);
      }
    } else {
      // Opening a fresh "+ Onboard Vessel" form — ESD selections, the
      // assigned user, and any staged vessel photo must never carry over
      // from a previous session (whether that session was saved, or just
      // closed without saving).
      setSelectedEsds([]);
      setAssignedUserId(null);
      setAssignedUserEmail(null);
      setVesselImageBase64(null);
      setVesselImageFileName(null);
      setVesselImageError(null);
      setEditingId(null);
    }
    setModalOpen(true);
  };


  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  useEffect(() => {
    if (vessels.length === 0) {
      setSimulatingId(null);
      return;
    }

    const activeVesselExists = vessels.some(vessel => vessel.id === simulatingId);

    if (!activeVesselExists) {
      setSimulatingId(vessels[0].id);
    }
  }, [simulatingId, vessels]);

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    // Number fields should never accept a minus sign — strip it rather than
    // relying on the (easily bypassed) `min` attribute alone.
    const sanitized = type === 'number' ? value.replace(/-/g, '') : value;
    setFormData(prev => ({
      ...prev,
      [name]: sanitized
    }));
  };

  const addMachine = () => {
    setMachines(prev => [
      ...prev,
      createMachine("")
    ]);
  };

  const removeMachine = (machineIndex) => {
    if (machineIndex < 2) return; // Don't delete default machines

    setMachines(prev =>
      prev.filter((_, index) => index !== machineIndex)
    );
  };

  const addFuel = (machineIndex) => {
    setMachines(prev =>
      prev.map((machine, index) =>
        index === machineIndex
          ? {
            ...machine,
            fuels: [...machine.fuels, createFuel()]
          }
          : machine
      )
    );
  };

  const removeFuel = (machineIndex, fuelIndex) => {
    setMachines(prev =>
      prev.map((machine, index) => {
        if (index !== machineIndex) return machine;

        if (machine.fuels.length === 1) {
          return machine;
        }

        return {
          ...machine,
          fuels: machine.fuels.filter((_, i) => i !== fuelIndex)
        };
      })
    );
  };

  const updateMachineName = (machineIndex, value) => {
    setMachines(prev =>
      prev.map((machine, index) =>
        index === machineIndex
          ? { ...machine, machineName: value }
          : machine
      )
    );
  };

  const updateFuel = (
    machineIndex,
    fuelIndex,
    field,
    value
  ) => {
    // Consumption and price are numeric and should never go negative.
    const sanitized = (field === 'consumption' || field === 'price')
      ? String(value).replace(/-/g, '')
      : value;
    setMachines(prev =>
      prev.map((machine, index) => {
        if (index !== machineIndex) return machine;

        return {
          ...machine,
          fuels: machine.fuels.map((fuel, i) =>
            i === fuelIndex
              ? {
                ...fuel,
                [field]: sanitized
              }
              : fuel
          )
        };
      })
    );
  };


  const loadVessels = async () => {
    setVesselsLoading(true);
    setVesselsError(null);

    try {
      const result = await vesselAPI.getAll();

      if (!result.success) {
        setVesselsError(result.error);
        return;
      }

      const raw = result.data?.data || result.data;
      const vesselList = Array.isArray(raw) ? raw
        : raw?.vessels || raw?.results || [];

      setVessels(
        vesselList.map((item, index) =>
          mapApiVesselToLocal(item, index)
        )
      );

    } catch (err) {

      console.error("LOAD ERROR:", err);

    } finally {

      setVesselsLoading(false);

    }

  };


  const loadSimulationData = async (vesselId) => {
    try {

      const result = await vesselAPI.getSimulationMeta(vesselId);



      if (!result.success) {
        console.warn('Could not load simulation data:', result.error);
        return;
      }

      const data = result.data.data;

      setSimulationData(data);
      // NOTE: intentionally NOT calling setSelectedEsds here — that state
      // belongs solely to the onboarding form's ESD checklist. Writing this
      // vessel's recommended measures into it was leaking into the "+ Onboard
      // Vessel" form the next time it was opened.

    } catch (err) {
      console.warn('Could not load simulation data:', err);
    }
  };
  const saveVessel = async () => {
    if (!formData.vesselName || !formData.owner || !formData.imoNumber) {
      alert("Please fill in required fields");
      return;
    }
    setOnboardLoading(true);

    // Always re-fetch the user list right before saving — not just when a
    // user was already selected — so the assigned user's id is resolved
    // against the freshest data regardless of whether the "Assign To"
    // dropdown was ever opened this session. Re-matched by email since
    // that's stable even if the numeric id were to change.
    const freshUsers = await loadUsersIfNeeded({ force: true });
    let resolvedAssignedUserId = assignedUserId;
    if (assignedUserId != null) {
      const match = assignedUserEmail
        ? freshUsers.find(u => u.email === assignedUserEmail)
        : freshUsers.find(u => u.id === assignedUserId);
      if (match) {
        resolvedAssignedUserId = match.id;
      } else {
        console.warn('Assigned user not found in refreshed user list — sending last-known id:', assignedUserId);
      }
    }

    // selectedEsds already holds full ESD objects (merged in by toggleEsd),
    // not bare IDs — no need to re-look them up in ESD_LIBRARY here.
    const selectedEsdObjects = selectedEsds.filter(Boolean);

    const payload = {

      vessel: {
        name_of_owner: formData.owner,
        vessel_name: formData.vesselName,
        vessel_type: (formData.vesselType || "")
          .toLowerCase()
          .replace(/\s+/g, "_"),

        build_year: parseInt(formData.buildYear, 10) || 0,
        flag: formData.flag,
        classification_society: formData.classificationSociety,
        imo_number: formData.imoNumber,
        gross_tonnage: parseFloat(formData.grossTonnage) || 0,
        dead_weight: parseFloat(formData.deadWeight) || 0,
        // TODO: confirm this is the exact field name the backend expects.
        vessel_image_base64: vesselImageBase64 || undefined,
      },

      voyage_meta: {
        analysis_month: parseInt(formData.month, 10) || 0,
        analysis_year: parseInt(formData.year, 10) || 0,
        docking_month: parseInt(formData.dockMonth, 10) || 0,
        docking_year: formData.dockingYear ? parseInt(formData.dockingYear, 10) : undefined,
        common_impl_month: formData.commonImplMonth ? parseInt(formData.commonImplMonth, 10) : undefined,
        common_impl_year: formData.commonImplYear ? parseInt(formData.commonImplYear, 10) : undefined,
        sailing_days_per_year: parseInt(formData.sailingDays, 10) || 200,
        non_steaming_days_per_year: parseInt(formData.nonSteamingDays, 10) || 165,
        distance_nm: parseFloat(formData.distanceNm) || 60000,
        // parseFloat("0") is 0, which is falsy — `|| 30` would wrongly
        // override a genuine 0% entry, so only fall back on NaN/blank input.
        eu_voyages_percent: Number.isNaN(parseFloat(formData.euPct)) ? 30 : parseFloat(formData.euPct),
        eua_cost_usd: parseFloat(formData.euaCost) || 75,
        f_i: parseFloat(formData.fI) || 1.0,
        f_m: parseFloat(formData.fM) || 1.0,
        f_c: parseFloat(formData.fC) || 1.0,
        f_ivse: parseFloat(formData.fIvse) || 1.0,
      },

      machines: machines.map(machine => ({
        machine_name: machine.machineName,

        fuel_particulars: machine.fuels.map(fuel => ({
          fuel_name: fuel.fuelName,
          consumption_mt: parseFloat(fuel.consumption) || 0,
          fuel_price_usd_per_mt: parseFloat(fuel.price) || 0,
        })),
      })),

      esd_measures: selectedEsdObjects
        // Deduplicate by name — keep last occurrence (user's latest edit wins)
        .filter((esd, idx, arr) => arr.findIndex(e => e.name === esd.name) === idx)
        .map(esd => ({
          category: (esd.category || 'operations').toLowerCase(),
          name: esd.name,
          efficiency_gain_percent: parseFloat(esd.efficiency_gain_percent) || 0,
          cost_usd: parseFloat(esd.cost_usd) || 0,
          lead_time_months: parseInt(esd.lead_time_months, 10) || 4,
          installation_req: esd.installation_req || 'in_sailing',
        })),
      vessel_life_years: parseInt(formData.vesselLifeYears, 10) || 25,
      vessel_end_year: formData.vesselEndYear ? parseInt(formData.vesselEndYear, 10) : undefined,
      vessel_end_month: formData.vesselEndMonth ? parseInt(formData.vesselEndMonth, 10) : undefined,
      discount_rate: parseFloat(formData.discountRate) || 0.10,
      assign_to_user_id: resolvedAssignedUserId || undefined,
    };

    try {

      // Step 1: Delete existing reports for this vessel (if re-onboarding)
      if (formData.imoNumber) {
        // Find existing vessel by IMO to get its ID
        const existingVessel = vessels.find(v => v.imoNumber === formData.imoNumber);
        if (existingVessel?.id) {
          await simulationAPI.deleteReports(existingVessel.id);
        }
      }

      // Step 2: Onboard the vessel
      const response = await onboardingAPI.onboardVessel(payload);

      if (!response.success) {
        alert(response.error || "Failed to onboard vessel");
        return;
      }

      // Get the vessel ID from the response
      const newVesselId = response.data?.data?.vessel_id || response.data?.vessel_id;

      // Auto-simulate to create the base report
      if (newVesselId) {
        try {
          const simResult = await simulationAPI.simulate(payload, payload.esd_measures || [], payload.vessel_life_years, payload.discount_rate);
          if (!simResult.success) {
            console.warn('Auto-simulate failed (base report not created):', simResult.error);
          }
        } catch (simErr) {
          console.warn('Auto-simulate error:', simErr.message);
        }
      }

      await loadVessels();
      closeModal();
      // Vessel saved successfully — clear the draft (including selected
      // ESDs) so the next "+ Onboard Vessel" starts from a blank form
      // instead of carrying over this vessel's selections.
      resetForm();
    } catch (error) {

      console.error("Onboard Vessel Error:", error);

      alert(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Something went wrong."
      );

    } finally {
      setOnboardLoading(false);
    }
  };

  const deleteVessel = async (vesselId) => {
    const vessel = vessels.find(v => v.id === vesselId);
    if (!window.confirm(`Delete "${vessel?.vesselName || 'this vessel'}" and all its reports?\n\nThis cannot be undone.`)) return;
    try {
      // 1. Delete all reports for this vessel
      await simulationAPI.deleteReports(vesselId);
      // 2. Delete the vessel itself
      await makeRequest('POST', '/home/delete-vessel/', { vessel_id: vesselId });
      // 3. Refresh vessel list from API
      await loadVessels();
    } catch (err) {
      console.error('[deleteVessel] Error:', err);
      alert('Failed to delete vessel: ' + (err.response?.data?.message || err.message));
    }
  };

  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [sessionMode, setSessionMode] = useState('base');
  const [vesselReports, setVesselReports] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);

  const openSimulator = async (vesselId) => {
    setSimulatingId(vesselId);
    setSessionMode('base');
    setSessionLoading(true);
    setSessionModalOpen(true);

    // Fetch reports for this vessel to show base/latest options
    try {
      const reportsResult = await simulationAPI.listReports(vesselId);
      if (reportsResult.success) {
        const reports = reportsResult.data?.data || reportsResult.data || [];
        setVesselReports(Array.isArray(reports) ? reports : []);
      } else {
        setVesselReports([]);
      }
    } catch (err) {
      console.warn('Could not fetch reports:', err.message);
      setVesselReports([]);
    }
    setSessionLoading(false);
  };

  const [initialReport, setInitialReport] = useState(null);

  const confirmSession = async () => {
    setSessionModalOpen(false);
    setSessionLoading(true);

    let reportToLoad = null;

    if (vesselReports.length > 0) {
      if (sessionMode === 'base' || vesselReports.length === 1) { reportToLoad = vesselReports[0]; } else { reportToLoad = vesselReports[vesselReports.length - 1]; }

      // If we have a report_id, fetch full report data
      if (reportToLoad?.report_id) {
        try {
          const fullReport = await simulationAPI.getReport(reportToLoad.report_id);
          if (fullReport.success) {
            reportToLoad = fullReport.data?.data || fullReport.data;
          }
        } catch (err) {
          console.warn('Could not fetch full report:', err.message);
        }
      }
    }

    setInitialReport(reportToLoad);
    setSessionLoading(false);
    setActiveTab("simulator");
  };
  const handleExportPdf = () => {
    if (!vessel) {
      return;
    }

    const previousTitle = document.title;
    document.title = `${vessel.vesselName || 'Vessel'} Report`;
    window.print();
    document.title = previousTitle;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  const formatCurrencyShort = (value) => {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);

    if (abs >= 1000000) {
      return `${sign}$${(abs / 1000000).toFixed(1)}M`;
    }

    if (abs >= 1000) {
      return `${sign}$${(abs / 1000).toFixed(0)}K`;
    }

    return `${sign}$${abs.toFixed(0)}`;
  };

  const formatMillions = (value) => `$${(value / 1000000).toFixed(1)}M`;

  const getCategoryColor = (category) => {
    const colors = {
      HULL: '#1D9E75',
      ENGINE: '#D97706',
      PROPULSION: '#2C6FBF',
      OPERATIONS: '#8B5CF6',
      AUXILIARY: '#F59E0B',
      MONITORING: '#EC4899',
    };
    return colors[category] || '#9CA3AF';
  };

  const toggleEsd = (id) => {

    const exists = selectedEsds.find(esd => esd.id === id);

    if (exists) {
      setSelectedEsds(prev =>
        prev.filter(esd => esd.id !== id)
      );
      return;
    }

    const selected = ESD_LIBRARY.find(esd => esd.id === id);

    setSelectedEsds(prev => [
      ...prev,
      {
        ...selected,
        // User-editable fields — these are what the payload reads
        efficiency_gain_percent: selected.saving || 0,
        cost_usd: selected.capex || 0,
        lead_time_months: selected.lead_time_months || 4,
        installation_req: selected.installation_req || 'in_sailing',
        // Clear library-only keys to avoid || priority confusion in payload
        saving: undefined,
        capex: undefined,
      }
    ]);

  };

  const removeSelectedEsd = (esdId) => {
    setSelectedEsds(prev => prev.filter(id => id !== esdId));
  };

  const getSimulatingVessel = () => vessels.find(v => v.id === simulatingId);

  // Financial calculations
  const totalCapex =
    selectedEsds.reduce(
      (sum, esd) => sum + Number(esd.cost_usd || 0),
      0
    );
  const totalSaving =
    selectedEsds.reduce(
      (sum, esd) => sum + Number(esd.efficiency_gain_percent || 0),
      0
    ); const vessel = getSimulatingVessel();
  const annualFuel = vessel ? ((vessel.costDO || 0) + (vessel.costLFO || 0) + (vessel.costHFO || 0)) * 100 : 0;
  const annualValue = annualFuel > 0 ? (annualFuel * totalSaving / 100) : 30420;
  const payback = annualValue > 0 ? (totalCapex / annualValue).toFixed(1) : 0;
  const npv = annualValue * 10 - totalCapex;
  const accumulatedSavings = annualValue * 2.5;
  const savingsPV = annualValue * 8;

  const investmentData = [
    { name: 'Total Cost', value: totalCapex },
    { name: 'Accum. Savings', value: -accumulatedSavings },
    { name: 'NPV', value: Math.max(npv, 0) },
    { name: 'Savings PV', value: savingsPV },
  ];

  const cashFlowData = Array.from({ length: 13 }, (_, idx) => {
    const year = 2025 + idx;
    return {
      year,
      cashFlow: totalCapex + annualValue * idx,
    };
  });

  const opexData = [
    { year: 2025, savings: annualValue, euSavings: annualValue * 0.3 },
    { year: 2026, savings: annualValue, euSavings: annualValue * 0.3 },
    { year: 2027, savings: annualValue, euSavings: annualValue * 0.3 },
    { year: 2028, savings: annualValue, euSavings: annualValue * 0.35 },
    { year: 2029, savings: annualValue, euSavings: annualValue * 0.35 },
    { year: 2030, savings: annualValue, euSavings: annualValue * 0.35 },
    { year: 2031, savings: annualValue, euSavings: annualValue * 0.4 },
    { year: 2032, savings: annualValue, euSavings: annualValue * 0.4 },

  ];

  const CATEGORY_NAMES = {
    hull: "Hydrodynamic Upgrades",
    propulsion: "Hydrodynamic Upgrades",

    engine: "Main Engine Upgrades",

    auxiliary: "Electrical Upgrades",

    operations: "Thermodynamic Upgrades",
  };

  const groupedEsds = {};

  ESD_LIBRARY.forEach((esd) => {
    const key = CATEGORY_NAMES[esd.category.toLowerCase()] || esd.category;

    if (!groupedEsds[key]) {
      groupedEsds[key] = [];
    }

    groupedEsds[key].push(esd);
  });

  return (
    <div className="tracker-wrapper">
      {/* Navigation */}
      <nav className="nav">
        <a href="#" className="nav-brand">
          {/* <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <rect width="22" height="22" rx="5" fill="#1D9E75" />
            <path d="M4 13 Q11 8 18 13" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" />
            <path d="M4 13 L6 17 L16 17 L18 13" stroke="white" stroke-width="1.2" fill="rgba(255,255,255,.15)" stroke-linejoin="round" />
            <ellipse cx="6" cy="15.5" rx="1.5" ry="1.5" fill="white" opacity=".6" />
          </svg> */}
          <img src="/Swoosh.png" alt="Azolla Logo" style={{ width: 22, height: 22, marginBottom: 4 }} />
          <span>AZOLLA ESD PLATFORM</span>
          <span className="nav-badge">Decarbonisation Suite</span>
        </a>
        <div className="nav-tabs">
          <button
            className={`nav-tab ${activeTab === 'vessels' ? 'on' : ''}`}
            onClick={() => setActiveTab('vessels')}
          >
            Vessels
          </button>
          <button
            className={`nav-tab ${activeTab === 'simulator' ? 'on' : ''}`}
            onClick={() => { if (simulatingId) setActiveTab('simulator'); }}
            style={activeTab === 'simulator' ? { background: '#1D9E75', color: '#fff', borderRadius: 6, fontWeight: 600 } : {}}
          >
            {getSimulatingVessel()?.vesselName || 'ESD Simulator'}
          </button>
        </div>
        <div className="nav-right">
          <span className="user-email">{userEmail}</span>
          <button className="btn btn-secondary" onClick={onLogout}>
            Logout
          </button>
        </div>
      </nav>

      {/* ===== SIMULATOR — rendered outside container for full width ===== */}
      {activeTab === 'simulator' && (
        <>
          {autoExportPdfPending && (
            <div style={{
              position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 2000, background: '#1E3A5F', color: '#fff', padding: '8px 16px',
              borderRadius: 8, fontSize: 12, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,.2)',
            }}>
              ⏳ Generating your PDF report… you'll be returned automatically
            </div>
          )}
          <SimulationWorkspace
            vesselId={simulatingId}
            vesselName={getSimulatingVessel()?.vesselName}
            sessionMode={sessionMode}
            initialReport={initialReport}
            isOnlyReport={vesselReports.length === 1}
            vesselReports={vesselReports}
            onBack={() => setActiveTab('vessels')}
            isAdmin={isAdmin}
            autoExportPdf={autoExportPdfPending}
            onAutoExportDone={() => {
              setAutoExportPdfPending(false);
              setPdfGeneratingId(null);
              setActiveTab('vessels');
            }}
          />
        </>
      )}

      {/* Main Content (vessels page only) */}
      {activeTab !== 'simulator' && (
        <div className="container">
          {/* ===== VESSELS PAGE ===== */}
          {activeTab === 'vessels' && (
            <div className="page-content">
              <div className="sec-hd">
                <div>
                  <div className="sec-title">Vessel Fleet</div>
                  <div className="sec-sub">Manage and track vessels onboarded to the platform</div>
                </div>
                {isAdmin && (
                  <button className="btn btn-primary" onClick={() => openOnboardModal()}>
                    + Onboard Vessel
                  </button>
                )}
              </div>

              <div className="card">
                <div className="card-hd">
                  <span className="card-title">Vessels ({vessels.length})</span>
                </div>
                <div className="card-body">
                  {vesselsLoading ? (
                    <div className="empty-state">
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                      Loading vessels…
                    </div>
                  ) : vesselsError ? (
                    <div className="empty-state">
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
                      Couldn't load vessels: {vesselsError}
                    </div>
                  ) : vessels.length === 0 ? (
                    <div className="empty-state">
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚢</div>
                      No vessels onboarded yet
                    </div>
                  ) : (
                    <table className="vessel-table">
                      <thead>
                        <tr>
                          <th>Vessel</th>
                          <th>Type</th>
                          <th>IMO</th>
                          <th>DWT</th>
                          <th>GT</th>
                          <th>Build Year</th>
                          <th>Sailing Days</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vessels.map((vessel) => {
                          const ageYrs = new Date().getFullYear() - vessel.buildYear;
                          return (
                            <tr key={vessel.id}>
                              <td>
                                <div className="vessel-row-name">{vessel.vesselName}</div>
                                <div className="vessel-row-sub">
                                  {vessel.owner} · {vessel.flag} · Built {vessel.buildYear} ({ageYrs}yr)
                                </div>
                              </td>
                              <td>
                                <span className="badge badge-blue">{vessel.vesselType}</span>
                              </td>
                              <td className="mono">{vessel.imoNumber}</td>
                              <td>{formatNumber(vessel.deadWeight)} t</td>
                              <td>{formatNumber(vessel.grossTonnage)} t</td>
                              <td>{vessel.buildYear} <span style={{ fontSize: 10, color: '#9CA3AF' }}>({ageYrs}yr)</span></td>
                              <td>{vessel.sailingDays || '—'} days</td>
                              <td>
                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    title="View all reports"
                                    onClick={() => openReportsModal(vessel)}
                                  >
                                    📋 Reports
                                  </button>
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={async () => {
                                      await loadSimulationData(vessel.id);
                                      openSimulator(vessel.id);
                                    }}
                                  >
                                    ⚙️ Simulate
                                  </button>
                                  {/* Edit commented out
                                {isAdmin && (
                                  <button className="btn btn-secondary btn-sm"
                                    onClick={() => openOnboardModal(vessel.id)}>✏️ Edit</button>
                                )} */}
                                  <button
                                    className="btn btn-danger btn-sm"
                                    title="Delete vessel"
                                    onClick={() => deleteVessel(vessel.id)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      )} {/* end container for non-simulator pages */}

      {/* ===== MODAL: Start Session ===== */}
      {sessionModalOpen && (
        <div className="modal-overlay" onClick={() => setSessionModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 'min(480px, 90vw)', width: '100%', maxHeight: '90vh', minHeight: '45vh', overflowY: 'auto', margin: '5vh auto' }}>
            <div style={{ padding: 'clamp(14px, 3vw, 20px) clamp(16px, 3vw, 24px)' }}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>Start Simulation Session</div>
              <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 14 }}>
                {getSimulatingVessel()?.vesselName || '—'} · IMO {getSimulatingVessel()?.imoNumber || '—'}
              </div>

              <div
                onClick={() => setSessionMode('base')}
                style={{ border: sessionMode === 'base' ? '2px solid #1D9E75' : '1px solid var(--bd)', borderRadius: 6, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', background: sessionMode === 'base' ? '#FEFCE8' : '#fff' }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 16 }}>📋</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>Start from base report</div>
                    <div style={{ fontSize: 10, color: 'var(--ink3)', marginTop: 1 }}>Use original onboarding data. A <b>new report ID</b> is issued on first run.</div>
                  </div>
                </div>
              </div>

              <div
                onClick={() => setSessionMode('last')}
                style={{
                  border: sessionMode === 'last' ? '2px solid #1D9E75' : '1px solid var(--bd)', borderRadius: 6, padding: '10px 12px', marginBottom: 12, cursor: 'pointer', background: sessionMode === 'last' ? '#FEFCE8' : '#fff', opacity: vesselReports.length > 1 ? 1 : 0.5, pointerEvents: vesselReports.length > 1 ? 'auto' : 'none'
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 16 }}>🔄</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>Continue from last simulation</div>
                    <div style={{ fontSize: 10, color: 'var(--ink3)', marginTop: 1 }}>
                      {vesselReports.length > 0 ? (
                        <>Last report: <b style={{ color: '#1D9E75', fontFamily: 'monospace' }}>{vesselReports[vesselReports.length - 1]?.report_id || '—'}</b>. All edits update this same ID.</>
                      ) : (
                        <>No previous reports found. Onboard the vessel first.</>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 5, padding: '6px 10px', fontSize: 9, color: '#92400E', marginBottom: 8 }}>
                <b>Session lock:</b> All edits update the <b>same report ID</b>. A new ID only generates when starting from base.
              </div>
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 5, padding: '6px 10px', fontSize: 9, color: '#1E40AF' }}>
                <b>Editable:</b> Sailing days · Non-sailing days · EUA cost · Fuel consumption &amp; prices · ESD selection
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button className="btn btn-secondary" onClick={() => setSessionModalOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={confirmSession} disabled={sessionLoading}>
                  {sessionLoading ? '⏳ Loading…' : '▶ Start Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: Reports List ===== */}
      {reportsModalOpen && (
        <div className="modal-overlay" onClick={() => setReportsModalOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 780, width: '95%' }}>
            <div className="modal-hd">
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Reports — {reportsVessel?.vesselName}</div>
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2 }}>All simulation reports for this vessel</div>
              </div>
              <button className="modal-close" onClick={() => setReportsModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: '12px 20px 20px', maxHeight: 480, overflowY: 'auto' }}>
              {reportsLoading ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--ink3)' }}>Loading reports…</div>
              ) : reportsList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--ink3)' }}>No reports found</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)', fontWeight: 500 }}>Report ID</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--ink3)', fontWeight: 500 }}>Created</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--ink3)', fontWeight: 500 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsList.map((report, idx) => (
                      <tr key={report.report_id || idx} style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: 11 }}>{report.report_id || '—'}</td>
                        <td style={{ padding: '8px', color: 'var(--ink3)' }}>{report.created_at ? new Date(report.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              disabled={pdfGeneratingId === (report.report_id || report.id)}
                              onClick={() => downloadReport(report, reportsVessel)}
                            >
                              {pdfGeneratingId === (report.report_id || report.id) ? '⏳ Generating…' : '⬇ Download PDF'}
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={() => simulateFromReport(report, reportsVessel)}>⚙️ Simulate</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL: Onboard Vessel ===== */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ height: "90vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-hd">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {editingId ? 'Edit Vessel' : 'Onboard New Vessel'}
                </div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
                  Fill in the vessel details. Yellow-highlighted fields are required.
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: 4, alignSelf: 'flex-start' }}
                  title="Clear all entered vessel details, fuel particulars, and selected ESDs"
                  onClick={() => {
                    if (window.confirm('Reset the form? This clears all entered vessel details, fuel particulars, and selected ESDs.')) {
                      resetForm();
                    }
                  }}
                >
                  ↺ Reset
                </button>
              </div>
              <button className="btn btn-ghost" onClick={closeModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="onboard-card">
                <div className="onboard-title">
                  🖼️ Vessel Photo
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink3)', marginBottom: 8 }}>
                  Shown on the cover page of the PDF report for this vessel.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {vesselImageBase64 ? (
                    <img
                      src={vesselImageBase64}
                      alt="Vessel preview"
                      style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border2)' }}
                    />
                  ) : (
                    <div style={{
                      width: 96, height: 64, borderRadius: 6, border: '1px dashed var(--border2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, color: 'var(--ink3)', textAlign: 'center', padding: 4,
                    }}>
                      No photo
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', width: 'fit-content' }}>
                      {vesselImageBase64 ? 'Replace Image' : '⬆ Upload Image'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleVesselImageSelect}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {vesselImageFileName && (
                      <span style={{ fontSize: 10, color: 'var(--ink3)' }}>{vesselImageFileName}</span>
                    )}
                    {vesselImageBase64 && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ width: 'fit-content', color: 'var(--red)' }}
                        onClick={() => { setVesselImageBase64(null); setVesselImageFileName(null); }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                {vesselImageError && (
                  <div style={{ fontSize: 10, color: 'var(--red)', marginTop: 6 }}>{vesselImageError}</div>
                )}
              </div>
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">📅 Analysis Period</div>
                  <div className="form-grid form-grid-3">
                    <div className="form-group">
                      <label className="form-label">Analysis Month <span className="req">*</span></label>
                      <select
                        name="month"
                        value={formData.month}
                        onChange={handleFormChange}
                        className="form-input"
                      >
                        <option value="">Select month</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Analysis Year <span className="req">*</span></label>
                      <input
                        type="number"
                        name="year"
                        value={formData.year}
                        onChange={handleFormChange}
                        className="form-input"
                        min="2020"
                        max="2040"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Docking Month <span className="req">*</span></label>
                      <select
                        name="dockMonth"
                        value={formData.dockMonth}
                        onChange={handleFormChange}
                        className="form-input"
                      >
                        <option value="">Select month</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">🏷️ Vessel Identification</div>
                  <div className="form-grid form-grid-3">
                    <div className="form-group">
                      <label className="form-label">Name of Owner <span className="req">*</span></label>
                      <input
                        type="text"
                        name="owner"
                        value={formData.owner}
                        onChange={handleFormChange}
                        placeholder="e.g. NYK Line"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vessel Name <span className="req">*</span></label>
                      <input
                        type="text"
                        name="vesselName"
                        value={formData.vesselName}
                        onChange={handleFormChange}
                        placeholder="e.g. Tenjun"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vessel Type <span className="req">*</span></label>
                      <select
                        name="vesselType"
                        value={formData.vesselType}
                        onChange={handleFormChange}
                        className="form-input"
                      >
                        <option value="">Select type</option>
                        <option>Tanker</option>
                        <option>Bulk Carrier</option>
                        <option>Container</option>
                        <option>General Cargo</option>
                        <option>LNG Carrier</option>
                        <option>RORO</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Build Year <span className="req">*</span></label>
                      <input
                        type="number"
                        name="buildYear"
                        value={formData.buildYear}
                        onChange={handleFormChange}
                        placeholder="2008"
                        className="form-input"
                        min="1960"
                        max="2030"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Flag <span className="req">*</span></label>
                      <input
                        type="text"
                        name="flag"
                        value={formData.flag}
                        onChange={handleFormChange}
                        placeholder="e.g. Panama"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Classification Society <span className="req">*</span></label>
                      <input
                        list="class-society-list"
                        name="classificationSociety"
                        value={formData.classificationSociety}
                        onChange={handleFormChange}
                        placeholder="Type or select"
                        className="form-input"
                        autoComplete="off"
                      />
                      <datalist id="class-society-list">
                        <option value="ABS">American Bureau of Shipping</option>
                        <option value="DNV">Det Norske Veritas</option>
                        <option value="LR">Lloyd's Register</option>
                        <option value="BV">Bureau Veritas</option>
                        <option value="ClassNK">Nippon Kaiji Kyokai</option>
                        <option value="KR">Korean Register</option>
                        <option value="CCS">China Classification Society</option>
                        <option value="RINA">Registro Italiano Navale</option>
                        <option value="IRS">Indian Register of Shipping</option>
                        <option value="RS">Russian Maritime Register</option>
                        <option value="PRS">Polish Register of Shipping</option>
                        <option value="CRS">Croatian Register of Shipping</option>
                        <option value="IACS">Multiple (IACS member)</option>
                      </datalist>
                      <div className="form-hint">Type to search or select. Can enter custom value.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">IMO Number <span className="req">*</span></label>
                      <input
                        type="text"
                        name="imoNumber"
                        value={formData.imoNumber}
                        onChange={handleFormChange}
                        placeholder="9343390"
                        className="form-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Gross Tonnage (GT) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="grossTonnage"
                        value={formData.grossTonnage}
                        onChange={handleFormChange}
                        placeholder="159927"
                        className="form-input"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Dead Weight (DWT) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="deadWeight"
                        value={formData.deadWeight}
                        onChange={handleFormChange}
                        placeholder="302107"
                        className="form-input"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Vessel Condition Profile */}
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">📊 Vessel Condition Profile</div>
                  <div className="form-grid form-grid-4">
                    <div className="form-group">
                      <label className="form-label">Sailing Days/Year <span className="req">*</span></label>
                      <input
                        type="number"
                        name="sailingDays"
                        value={formData.sailingDays}
                        onChange={handleFormChange}
                        placeholder="200"
                        className="form-input"
                        min="0"
                      />
                      <div className="form-hint">Total operating days at sea</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Non-steaming Days/Year <span className="req">*</span></label>
                      <input
                        type="number"
                        name="nonSteamingDays"
                        value={formData.nonSteamingDays}
                        onChange={handleFormChange}
                        placeholder="165"
                        className="form-input"
                        min="0"
                      />
                      <div className="form-hint">At port / idle</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Distance (NM/Year) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="distanceNm"
                        value={formData.distanceNm}
                        onChange={handleFormChange}
                        placeholder="60000"
                        className="form-input"
                        min="0"
                      />
                      <div className="form-hint">Annual voyage distance for CII</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">% EU Voyages <span className="req">*</span></label>
                      <input
                        type="number"
                        name="euPct"
                        value={formData.euPct}
                        onChange={handleFormChange}
                        placeholder="30"
                        className="form-input"
                        min="0"
                      />
                      <div className="form-hint">For ETS / EUA calculation</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">EUA Cost (USD/t) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="euaCost"
                        value={formData.euaCost}
                        onChange={handleFormChange}
                        placeholder="75"
                        className="form-input"
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Vessel Life (Years)</label>
                      <input
                        type="number"
                        name="vesselLifeYears"
                        value={formData.vesselLifeYears}
                        onChange={handleFormChange}
                        placeholder="25"
                        className="form-input"
                        min="5" max="40"
                      />
                      <div className="form-hint">For payback / NPV calc</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Discount Rate</label>
                      <input
                        type="number"
                        name="discountRate"
                        value={formData.discountRate}
                        onChange={handleFormChange}
                        placeholder="0.10"
                        className="form-input"
                        step="0.01" min="0" max="1"
                      />
                      <div className="form-hint">For NPV (e.g. 0.10 = 10%)</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charter End / Docking Schedule / Common Implementation */}
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">📅 Charter End &amp; Implementation Schedule</div>
                  <div className="form-grid form-grid-3">
                    <div className="form-group">
                      <label className="form-label">Charter End Year</label>
                      <input type="number" name="vesselEndYear" value={formData.vesselEndYear} onChange={handleFormChange} placeholder="e.g. 2029" className="form-input" min="0" />
                      <div className="form-hint">Leave blank to use Vessel Life</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Charter End Month</label>
                      <input type="number" name="vesselEndMonth" value={formData.vesselEndMonth} onChange={handleFormChange} placeholder="1–12" className="form-input" min="1" max="12" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Docking Year</label>
                      <input type="number" name="dockingYear" value={formData.dockingYear} onChange={handleFormChange} placeholder="e.g. 2027" className="form-input" min="0" />
                      <div className="form-hint">First scheduled docking year</div>
                    </div>
                  </div>
                  <div className="form-grid form-grid-2" style={{ marginTop: 8 }}>
                    <div className="form-group">
                      <label className="form-label">Common ESD Impl. Month</label>
                      <input type="number" name="commonImplMonth" value={formData.commonImplMonth} onChange={handleFormChange} placeholder="1–12" className="form-input" min="1" max="12" />
                      <div className="form-hint">Force all in-sailing ESDs to this month (optional)</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Common ESD Impl. Year</label>
                      <input type="number" name="commonImplYear" value={formData.commonImplYear} onChange={handleFormChange} placeholder="e.g. 2026" className="form-input" min="0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* CII Correction Factors */}
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">📐 CII Correction Factors <span style={{ fontSize: 10, fontWeight: 400, color: '#9CA3AF' }}>(leave at 1.0 for standard vessels)</span></div>
                  <div className="form-grid form-grid-4">
                    <div className="form-group">
                      <label className="form-label">f_i (Capacity)</label>
                      <input type="number" name="fI" value={formData.fI} onChange={handleFormChange} placeholder="1.0" className="form-input" step="0.01" min="0" />
                      <div className="form-hint">Capacity correction</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">f_m (Ice Class)</label>
                      <input type="number" name="fM" value={formData.fM} onChange={handleFormChange} placeholder="1.0" className="form-input" step="0.01" min="0" />
                      <div className="form-hint">Ice-class correction</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">f_c (Shuttle)</label>
                      <input type="number" name="fC" value={formData.fC} onChange={handleFormChange} placeholder="1.0" className="form-input" step="0.01" min="0" />
                      <div className="form-hint">Shuttle tanker correction</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">f_ivse (EEDI/VSE)</label>
                      <input type="number" name="fIvse" value={formData.fIvse} onChange={handleFormChange} placeholder="1.0" className="form-input" step="0.01" min="0" />
                      <div className="form-hint">Voluntary structural enhancement</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fuel Particulars */}
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">
                    ⛽ Fuel Particulars
                  </div>

                  {machines.map((machine, machineIndex) => (

                    <div className="fuel-machine-card" key={machineIndex}>

                      <div className="fuel-machine-header">

                        {machineIndex < 2 ? (

                          <h4>{machine.machineName}</h4>

                        ) : (

                          <input
                            className="fuel-machine-name-input"
                            value={machine.machineName}
                            placeholder="Machine name (e.g. Boiler)"
                            onChange={(e) =>
                              updateMachineName(machineIndex, e.target.value)
                            }
                          />

                        )}

                        <div>

                          <button
                            type="button"
                            className="btn-add-fuel"
                            onClick={() => addFuel(machineIndex)}
                          >
                            + Add fuel
                          </button>

                          {machineIndex >= 2 && (
                            <button
                              type="button"
                              className="btn-remove-machine"
                              onClick={() => removeMachine(machineIndex)}
                            >
                              🗑
                            </button>
                          )}

                        </div>

                      </div>

                      <div className="fuel-col-labels">
                        <span>Fuel name</span>
                        <span>Consumption (MT)</span>
                        <span>Price (USD/MT)</span>
                        <span></span>
                      </div>

                      {machine.fuels.map((fuel, fuelIndex) => (

                        <div
                          className="fuel-row"
                          key={fuelIndex}
                        >

                          <Select
                            value={fuel.fuelName}
                            placeholder="Select fuel"

                            listHeight={280}
                            virtual={false}
                            getPopupContainer={() => document.body}
                            onChange={(value) => updateFuel(machineIndex, fuelIndex, 'fuelName', value)}
                          >
                            <Option value="HFO">HFO</Option>
                            <Option value="VLSFO">VLSFO</Option>
                            <Option value="ULSFO">ULSFO</Option>
                            <Option value="LFO">LFO</Option>
                            <Option value="MDO">MDO</Option>
                            <Option value="LPG-AVERAGE">LPG-AVERAGE</Option>
                            <Option value="LNG-BOILER">LNG-BOILER</Option>
                            <Option value="LNG-OTHERS">LNG-OTHERS</Option>
                            <Option value="LNG-AE">LNG-AE</Option>
                            <Option value="LNG-ME">LNG-ME</Option>
                            <Option value="LPG-BUTANE">LPG-BUTANE</Option>
                            <Option value="LPG-PROPANE">LPG-PROPANE</Option>
                            <Option value="METHANOL">METHANOL</Option>
                            <Option value="ETHANOL">ETHANOL</Option>
                          </Select>

                          <input
                            placeholder="0.0"
                            value={fuel.consumption}
                            onChange={(e) =>
                              updateFuel(
                                machineIndex,
                                fuelIndex,
                                "consumption",
                                e.target.value
                              )
                            }
                          />

                          <input
                            placeholder="0"
                            value={fuel.price}
                            onChange={(e) =>
                              updateFuel(
                                machineIndex,
                                fuelIndex,
                                "price",
                                e.target.value
                              )
                            }
                          />

                          <button
                            type="button"
                            className="fuel-row-delete"
                            onClick={() =>
                              removeFuel(machineIndex, fuelIndex)
                            }
                          >
                            🗑
                          </button>

                        </div>

                      ))}

                    </div>

                  ))}

                  <button
                    type="button"
                    className="btn-add-machine"
                    onClick={addMachine}
                  >
                    + Add machine
                  </button>

                </div>
              </div>

              {/* FEUM Penalty */}
              <div className="onboard-card">
                <div className="form-section">
                  <div className="form-section-title">⚠️ FEUM Penalty</div>
                  <div className="form-grid form-grid-2">
                    <div className="form-group">
                      <label className="form-label">FEUM Penalty (USD/yr) <span className="req">*</span></label>
                      <input
                        type="number"
                        name="feumPenalty"
                        value={formData.feumPenalty}
                        onChange={handleFormChange}
                        placeholder="83828"
                        className="form-input"
                        min="0"
                      />
                      <div className="form-hint">From FEUM Calculations sheet</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Current CII Rating</label>
                      <select
                        name="ciiRating"
                        value={formData.ciiRating}
                        onChange={handleFormChange}
                        className="form-input"
                      >
                        <option>A</option>
                        <option>B</option>
                        <option>C</option>
                        <option>D</option>
                        <option>E</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="onboard-card">

                <div className="onboard-title">
                  🍃 ESD Measures
                  <span className="selected-badge">
                    {selectedEsds.length} Selected
                  </span>
                </div>

                <div className="esd-col-labels">
                  <span>Measure</span>
                  <span>Efficiency gain (%)</span>
                  <span>Cost (USD)</span>
                </div>

                {Object.entries(groupedEsds).map(([category, measures]) => (

                  <div key={category} style={{ marginBottom: 20 }}>
                    <details
                      style={{
                        border: "1px solid #D9D9D9",
                        borderRadius: 8,
                        padding: "10px 14px",
                        background: "#fff"
                      }}
                    >

                      <summary
                        style={{
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: 15
                        }}
                      >
                        {category}
                      </summary>

                      <div
                        style={{
                          marginTop: 12,
                          display: "flex",
                          flexDirection: "column",
                          gap: 10
                        }}
                      >

                        {measures.map(item => (

                          <label
                            key={item.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              cursor: "pointer"
                            }}
                          >

                            <input
                              type="checkbox"
                              checked={selectedEsds.some(esd => esd.id === item.id)} onChange={() => toggleEsd(item.id)}
                            />

                            {item.name}

                          </label>

                        ))}

                      </div>

                    </details>

                    {measures
                      .filter(item =>
                        selectedEsds.some(esd => esd.id === item.id)
                      ).map(item => (

                        <div
                          key={item.id}
                          className="selected-esd-card"
                        >

                          <div className="selected-esd-header">

                            <div className="selected-esd-title">
                              {item.name}
                            </div>

                            <button
                              className="selected-esd-remove"
                              onClick={() => toggleEsd(item.id)}
                            >
                              ✕
                            </button>

                          </div>

                          <div className="selected-esd-fields">

                            <div className="selected-esd-field">
                              <label>Efficiency Gain (%)</label>
                              <input
                                value={selectedEsds.find(esd => esd.id === item.id)?.efficiency_gain_percent || ""}
                                readOnly={!isAdmin}
                                style={!isAdmin ? { background: 'var(--surface-1)', color: 'var(--ink3)', cursor: 'not-allowed' } : {}}
                                onChange={(e) => {
                                  if (!isAdmin) return;
                                  const v = e.target.value.replace(/-/g, '');
                                  setSelectedEsds(prev => prev.map(esd =>
                                    esd.id === item.id ? { ...esd, efficiency_gain_percent: v } : esd
                                  ));
                                }}
                              />
                            </div>

                            <div className="selected-esd-field">
                              <label>Cost (USD)</label>
                              <input
                                value={selectedEsds.find(esd => esd.id === item.id)?.cost_usd || ""}
                                readOnly={!isAdmin}
                                style={!isAdmin ? { background: 'var(--surface-1)', color: 'var(--ink3)', cursor: 'not-allowed' } : {}}
                                onChange={(e) => {
                                  if (!isAdmin) return;
                                  const v = e.target.value.replace(/-/g, '');
                                  setSelectedEsds(prev => prev.map(esd =>
                                    esd.id === item.id ? { ...esd, cost_usd: v } : esd
                                  ));
                                }}
                              />
                            </div>

                            <div className="selected-esd-field">
                              <label>Lead Time (months)</label>
                              <input
                                type="number" min="1" max="24"
                                value={selectedEsds.find(esd => esd.id === item.id)?.lead_time_months || ""}
                                readOnly={!isAdmin}
                                style={!isAdmin ? { background: 'var(--surface-1)', color: 'var(--ink3)', cursor: 'not-allowed' } : {}}
                                onChange={(e) => {
                                  if (!isAdmin) return;
                                  const v = e.target.value.replace(/-/g, '');
                                  setSelectedEsds(prev => prev.map(esd =>
                                    esd.id === item.id ? { ...esd, lead_time_months: v } : esd
                                  ));
                                }}
                              />
                            </div>

                            <div className="selected-esd-field">
                              <label>Installation</label>
                              <select
                                value={selectedEsds.find(esd => esd.id === item.id)?.installation_req || "in_sailing"}
                                disabled={!isAdmin}
                                style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 12, width: '100%', ...(!isAdmin ? { background: 'var(--surface-1)', color: 'var(--ink3)', cursor: 'not-allowed' } : {}) }}
                                onChange={(e) => {
                                  if (!isAdmin) return;
                                  setSelectedEsds(prev => prev.map(esd =>
                                    esd.id === item.id ? { ...esd, installation_req: e.target.value } : esd
                                  ));
                                }}
                              >
                                <option value="in_sailing">In-Sailing</option>
                                <option value="docking">Docking</option>
                              </select>
                            </div>

                          </div>

                        </div>

                      ))}

                  </div>

                ))}
              </div>

              <div className="onboard-card">
                <div className="onboard-title">
                  Assign To
                </div>

                <Select
                  showSearch
                  allowClear
                  placeholder="Select a user to assign this vessel to"
                  style={{ width: '100%' }}
                  value={assignedUserId ?? null}
                  loading={usersLoading}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onDropdownVisibleChange={(open) => {
                    if (open) loadUsersIfNeeded();
                  }}
                  onChange={(value) => {
                    setAssignedUserId(value ?? null);
                    const picked = userOptions.find(u => u.id === value);
                    setAssignedUserEmail(picked?.email || null);
                  }}
                  options={userOptions.map(u => ({
                    value: u.id,
                    label: u.first_name || u.last_name
                      ? `${u.first_name || ''} ${u.last_name || ''}`.trim() + (u.email ? ` (${u.email})` : '')
                      : (u.email || u.username || `User ${u.id}`),
                  }))}
                  notFoundContent={usersLoading ? 'Loading users…' : 'No users found'}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={saveVessel} disabled={onboardLoading}>
                  {onboardLoading ? '⏳ Saving...' : '✓ Save Vessel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Tracker;